import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterChipGroup from "@/components/base/FilterChipGroup";
import WorkCard from "@/components/feature/WorkCard";
import usePageMeta from "@/hooks/usePageMeta";
import { worksList } from "@/mocks/works";

const PAGE_STEP = 6;

const tabs = [
  { key: "all", label: "全部作品", path: "/works" },
  { key: "movies", label: "影视作品", path: "/works/movies" },
  { key: "music", label: "音乐作品", path: "/works/music" },
];

const metaMap: Record<string, { title: string; description: string; heading: string; sub: string }> = {
  all: {
    title: "森泽佳奈作品集 - 影视与音乐精选",
    description:
      "森泽佳奈作品集，收录电影、电视剧、专辑与单曲等作品，作品类型多样、介绍详尽，方便浏览与了解。",
    heading: "森泽佳奈作品集",
    sub: "收录影视与音乐两大领域的代表作品，支持按年份与类型筛选，快速找到你感兴趣的作品。",
  },
  movies: {
    title: "森泽佳奈影视作品 - 电影与电视剧精选",
    description: "森泽佳奈影视作品合集，涵盖电影与电视剧，包含主演角色、年份与剧情简介等详细信息。",
    heading: "影视作品",
    sub: "森泽佳奈参演的电影与电视剧作品，包含主演角色、导演与上映年份等详细信息。",
  },
  music: {
    title: "森泽佳奈音乐作品 - 专辑与单曲精选",
    description: "森泽佳奈音乐作品合集，涵盖个人专辑与单曲，包含发行年份、创作身份与曲目信息。",
    heading: "音乐作品",
    sub: "森泽佳奈发行的个人专辑与单曲作品，了解她的音乐创作轨迹与代表曲目。",
  },
};

export default function Works() {
  const { category } = useParams<{ category?: string }>();
  const activeKey = category === "movies" || category === "music" ? category : "all";
  const meta = metaMap[activeKey];

  usePageMeta({ title: meta.title, description: meta.description, keywords: "森泽佳奈作品,森泽佳奈影视,森泽佳奈音乐" });

  const [searchParams] = useSearchParams();
  const queryKeyword = searchParams.get("q") || "";

  const [year, setYear] = useState("all");
  const [type, setType] = useState("all");
  const [keyword, setKeyword] = useState(queryKeyword);
  const [visible, setVisible] = useState(PAGE_STEP);

  useEffect(() => {
    setKeyword(queryKeyword);
  }, [queryKeyword]);

  useEffect(() => {
    setYear("all");
    setType("all");
    setVisible(PAGE_STEP);
  }, [activeKey]);

  useEffect(() => {
    setVisible(PAGE_STEP);
  }, [year, type, keyword]);

  const categoryWorks = useMemo(() => {
    const target = activeKey === "movies" ? "movie" : activeKey === "music" ? "music" : "all";
    return worksList.filter((w) => (target === "all" ? true : w.category === target));
  }, [activeKey]);

  const yearOptions = useMemo(() => {
    const years = Array.from(new Set(categoryWorks.map((w) => String(w.year)))).sort((a, b) => Number(b) - Number(a));
    return [{ label: "全部年份", value: "all" }, ...years.map((y) => ({ label: `${y} 年`, value: y }))];
  }, [categoryWorks]);

  const typeOptions = useMemo(() => {
    const types = Array.from(new Set(categoryWorks.map((w) => w.type)));
    return [{ label: "全部类型", value: "all" }, ...types.map((t) => ({ label: t, value: t }))];
  }, [categoryWorks]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return categoryWorks.filter((item) => {
      if (year !== "all" && String(item.year) !== year) return false;
      if (type !== "all" && item.type !== type) return false;
      if (kw && !item.title.includes(kw) && !item.role.includes(kw)) return false;
      return true;
    });
  }, [categoryWorks, year, type, keyword]);

  const visibleItems = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setYear("all");
    setType("all");
    setKeyword("");
    setVisible(PAGE_STEP);
  };

  return (
    <div className="bg-background-50">
      <section className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb
            items={
              activeKey === "all"
                ? [{ label: "作品" }]
                : [{ label: "作品", path: "/works" }, { label: meta.heading }]
            }
          />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-4">
            {meta.heading}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl">{meta.sub}</p>
        </div>
      </section>

      <section className="px-4 md:px-8 py-8 md:py-10">
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 mb-5 w-fit max-w-full overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <Link
              key={tab.key}
              to={tab.path}
              className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                activeKey === tab.key
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </div>

        <div className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 mb-6 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 flex-wrap">
            <FilterChipGroup label="年份" options={yearOptions} value={year} onChange={setYear} />
            <FilterChipGroup label="类型" options={typeOptions} value={type} onChange={setType} />
          </div>
          <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3 py-2 md:max-w-sm focus-within:border-primary-300">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索作品名称或角色"
              className="bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 w-full"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setKeyword("")}
                className="text-foreground-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>
        </div>

        {visibleItems.length > 0 ? (
          <>
            <p className="text-sm text-foreground-500 mb-4">共 {filtered.length} 部作品</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {visibleItems.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8 md:mt-10">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_STEP)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  加载更多作品
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-16 md:py-20 border border-dashed border-background-300 rounded-lg">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-search-line text-3xl"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-900">没有符合条件的作品</h3>
            <p className="text-sm text-foreground-500 mt-2">试试调整年份或类型筛选，或清空关键词。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}
      </section>
    </div>
  );
}